import _ from 'lodash';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from 'components/common/button';
import connect from 'components/lib/connect';
import CancelAgentInboxPolling from 'actions/inbox/agent/cancel_agent_inbox_polling';
import InboxMenu from './inbox_menu';
import InboxList from './inbox_list_container';
import RequestAgentInbox from 'actions/inbox/agent/request_agent_inbox';
import RequestAgentInboxDeprecated from 'actions/inbox/agent/request_agent_inbox_deprecated';
import Spinner from 'components/common/spinner_two';
import useDevice from 'components/hooks/use_device';
import { Device } from 'scripts/lib/device_detector';
import TwoArrowsRight from 'components/lib/icons/two_arrows_right';
import TwoArrowsLeft from 'components/lib/icons/two_arrows_left';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import useWindowSize from 'components/hooks/use_window_size';
import useIsFeatureEnabled from 'components/hooks/use_is_feature_enabled';

export function SidebarInbox({
  hasConversationGeneral,
  collapsed,
  customerCount,
  hasLoadingError,
  isLoading,
  isUsingNewInbox,
  someItemHasFailedLoad,
  toggleCollapse,
}) {
  useRequestAgentInbox(hasConversationGeneral, isUsingNewInbox);
  const isPhone = useDevice() === Device.Phone;

  if (!hasConversationGeneral) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledSidebar collapsed={collapsed} data-aid="customerList-loading" isLoading={isLoading}>
        <Spinner data-aid="customerList-loading" />
      </StyledSidebar>
    );
  }

  if (hasLoadingError) {
    return <FailedLoadInbox />;
  }

  // On phone, always show the expanded inbox — the sidebar is the full-screen home.
  const showExpanded = isPhone || !collapsed || someItemHasFailedLoad;

  return showExpanded ? (
    <ExpandedInbox
      customerCount={customerCount}
      hasConversationGeneral={hasConversationGeneral}
      isPhone={isPhone}
      someItemHasFailedLoad={someItemHasFailedLoad}
      toggleCollapse={toggleCollapse}
    />
  ) : (
    <CollapsedInbox hasConversationGeneral={hasConversationGeneral} toggleCollapse={toggleCollapse} />
  );
}

function useRequestAgentInbox(hasConversationGeneral, isUsingNewInbox) {
  const executeAction = useExecuteAction();
  useEffect(() => {
    if (hasConversationGeneral) {
      if (!isUsingNewInbox) {
        executeAction(RequestAgentInboxDeprecated);
      } else {
        executeAction(RequestAgentInbox);
      }
      return () => {
        executeAction(CancelAgentInboxPolling);
      };
    }
    // We handle isUsingNewInbox changing in the feature set gateway observer, hence why we don't include it here.
  }, [hasConversationGeneral, executeAction]);
}

function ExpandedInbox({ customerCount, hasConversationGeneral, isPhone, someItemHasFailedLoad, toggleCollapse }) {
  const isPaymentOverdueBannerVisible = useIsFeatureEnabled()('paymentOverdueBanner');
  const { t } = useTranslation();

  return (
    <StyledSidebar
      $isPaymentOverdueBannerVisible={isPaymentOverdueBannerVisible}
      data-aid="customerList"
      data-collapsed="false"
    >
      <Inbox data-aid="customerList-scrollWrapper">
        <Header data-aid="customerList-header">
          <Title data-aid="customerList-title">
            <TitleText>
              {customerCount !== null && customerCount !== undefined
                ? t('{{count}} customers', { count: customerCount })
                : t('My Customers')}
            </TitleText>
            {isPhone && <PhoneLogo data-aid="customerList-logo" />}
          </Title>
          <InboxMenu hasConversationGeneral={hasConversationGeneral} />
        </Header>
        <InboxList />
      </Inbox>
      {!someItemHasFailedLoad && !isPhone ? (
        <Collapser data-aid="customerList-sizeToggle" onClick={toggleCollapse}>
          <TwoArrowsLeft />
        </Collapser>
      ) : null}
    </StyledSidebar>
  );
}

function CollapsedInbox({ hasConversationGeneral, toggleCollapse }) {
  const { windowWidth } = useWindowSize();
  const isPaymentOverdueBannerVisible = useIsFeatureEnabled()('paymentOverdueBanner');

  return (
    <StyledSidebar
      $isPaymentOverdueBannerVisible={isPaymentOverdueBannerVisible}
      collapsed
      data-aid="customerList"
      data-collapsed="true"
    >
      <Scroller data-aid="customerList-scrollWrapper">
        <InboxList collapsed />
        <InboxMenu collapsed hasConversationGeneral={hasConversationGeneral} />
      </Scroller>
      {windowWidth > 1280 ? (
        <Expander data-aid="customerList-sizeToggle" onClick={toggleCollapse}>
          <TwoArrowsRight />
        </Expander>
      ) : null}
    </StyledSidebar>
  );
}

function FailedLoadInbox() {
  const { t } = useTranslation();
  const executeAction = useExecuteAction();
  const rerequestInbox = () => {
    executeAction(RequestAgentInbox);
  };
  const isFeatureEnabled = useIsFeatureEnabled();
  const isPaymentOverdueBannerVisible = isFeatureEnabled('paymentOverdueBanner');

  return (
    <StyledSidebar
      $isPaymentOverdueBannerVisible={isPaymentOverdueBannerVisible}
      data-aid="customerList-failedLoad"
      data-collapsed="false"
    >
      <FailedInbox>
        <FailedContainer>
          <LoadErrorDescription>{t('The inbox failed to load')}</LoadErrorDescription>
          <Button onClick={rerequestInbox} type={Button.Types.PRIMARY}>
            {t('Retry')}
          </Button>
        </FailedContainer>
      </FailedInbox>
    </StyledSidebar>
  );
}

export function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const agentInboxNewProvider = getProvider('agentInboxItemsNew');

  const personalInboxCounts = getProvider('personalInboxCounts').get();
  const customerCount = personalInboxCounts ? personalInboxCounts.openCount + personalInboxCounts.waitingCount : null;
  const useNewInboxItems = true;

  const someItemHasFailedLoad = useNewInboxItems
    ? _.compact(
        _.map(agentInboxNewProvider.findAll(), item => {
          const errors = agentInboxNewProvider.getErrors(item.id);
          return errors && errors.length;
        })
      ).length > 0
    : false;

  return {
    hasConversationGeneral: isFeatureEnabled('conversation:general'),
    customerCount,
    // New inbox doesn't use polling so we should show a button which allows agents to manually re-fetch the inbox if there's a request timeout.
    hasLoadingError: agentInboxNewProvider.getErrorForLoading(),
    isLoading: agentInboxNewProvider.isLoading(),
    isUsingNewInbox: useNewInboxItems,
    // Likewise, if an individual item fails to load, we need to ensure we show the expanded inbox so that they can manually refetch an individual item.
    someItemHasFailedLoad,
  };
}

const SidebarInboxContainer = connect(mapStateToProps)(SidebarInbox);
export default SidebarInboxContainer;

const Title = styled.div`
  color: ${p => p.theme.colors.gray700};
  flex: 1;
  font-weight: bold;

  [data-device='phone'] & {
    position: relative;
  }
`;

const TitleText = styled.span``;

const PhoneLogo = styled.div.attrs({ className: 'page-logo' })`
  left: 50%;
  margin-top: 0;
  position: absolute;
  top: 4px;
  transform: translateX(-50%);
  width: 80px;
`;

const Header = styled.div`
  display: flex;
  padding: ${p => p.theme.spacing.insetXLarge} 16px 0 16px;
`;

const Inbox = styled.div`
  height: 100%;
  overflow: auto;
  position: relative;
`;

const loadingSidebar = css`
  align-items: center;
  display: flex;
  justify-content: center;
`;

const StyledSidebar = styled.div`
  background-color: ${p => p.theme.colors.white};
  box-shadow: ${p => p.theme.boxShadow.small};
  flex-shrink: 0;
  height: 100%;
  overflow: auto;
  padding-top: ${p => (p.$isPaymentOverdueBannerVisible ? '96px' : '60px')};
  position: relative;
  width: ${p => (p.collapsed ? `80px` : `340px`)};
  z-index: 6;
  ${p => p.isLoading && loadingSidebar}

  [data-device='phone'] & {
    width: 100%;
    box-shadow: none;
  }
`;

const Expander = styled.div`
  align-items: center;
  background-color: ${p => p.theme.colors.white};
  bottom: 0;
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
  box-shadow: var(--color-box-shadow-small);
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  display: flex;
  height: 24px;
  justify-content: center;
  margin-bottom: 16px;
  position: absolute;
  right: 0;
  width: 24px;

  &:hover {
    background-color: ${p => p.theme.colors.gray100};
  }

  svg {
    height: 14px;
  }
`;

const Scroller = styled.div`
  height: 100%;
  overflow: auto;
`;

const Collapser = Expander;

const FailedInbox = styled(Inbox)`
  align-items: center;
  display: flex;
  justify-content: center;
`;
const FailedContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
const LoadErrorDescription = styled.div`
  margin-bottom: 8px;
`;
